/**
 * Group Rewards Dialog
 * Manages reward templates for a group in a dialog that goes full-screen on mobile
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  Menu,
  ListItemIcon,
  Tooltip,
} from '@mui/material'
import {
  Close as CloseIcon,
  Add,
  CardGiftcard,
  CheckCircle,
  Cancel,
  ArrowBack,
  Save,
  Schedule,
  Redeem,
  SwapHoriz,
  People,
  Person,
  Delete as DeleteIcon,
  MoreVert,
  Edit,
  BarChart,
  Send,
  Pause,
  PlayArrow,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  GiftRewardContentType,
  DistributionType,
  ExpirationType,
  getContentTypeName,
  getDistributionTypeName,
  getExpirationTypeName,
  getInstanceStatusName,
} from '@api/types/rewards'
import type { GiftRewardTemplateDto, CreateGiftRewardTemplateRequest, CurrentHolderDto, DistributionConfig } from '@api/types/rewards'
import {
  useRewardTemplates,
  useRewardTemplate,
  useToggleTemplateActive,
  useDeleteTemplate,
  useCreateTemplate,
  useUpdateTemplate,
  useTemplateStats,
  useRedemptionHistory,
  useTransferHistory,
} from '../hooks/useRewardTemplates'
import { DistributeRewardsDialog } from './DistributeRewardsDialog'
import DistributionConfigEditor from './DistributionConfigEditor'
import {
  getStatusColor,
  getContentTypeIcon,
  getDistributionTypeIcon,
  getContentTypeColor,
} from '../utils/rewardHelpers'
import { ImageUploadZone } from '@/components/common/ImageUploadZone'
import { uploadsApi } from '@api/endpoints/uploads'
import { adventuresApi } from '@api/endpoints/adventures'
import type { AdventureDto } from '@features/adventures/types/adventure-editor.types'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import { TableRow as TiptapTableRow, TableCell as TiptapTableCell, TableHeader as TiptapTableHeader } from '@tiptap/extension-table'
import { BorderlessTable } from '@features/adventures/components/editor/extensions/BorderlessTable'
import { TipTapToolbar } from '@features/adventures/components/editor/panels/TipTapToolbar'
import { ResizableImage, ImageBubbleMenu } from '@features/adventures/components/editor/extensions/ResizableImage'
import { ImagePasteHandler } from '@features/adventures/components/editor/extensions/ImagePasteHandler'
import '@features/adventures/components/editor/styles/DescriptionPanel.css'

interface GroupRewardsDialogProps {
  open: boolean
  onClose: () => void
  groupId: string
}

type DialogView = 'list' | 'edit' | 'stats'
type FilterTab = 'all' | 'active' | 'inactive'
type SortColumn = 'title' | 'contentType' | 'distributionType' | 'distributed' | 'active'
type SortDirection = 'asc' | 'desc'

// Stats Card Component
function StatsCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode
  label: string
  value: number
  color?: string
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '2px solid',
        borderColor: color || 'divider',
        textAlign: 'center',
      }}
    >
      <Box sx={{ color: color || 'text.secondary', mb: 0.5 }}>{icon}</Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  )
}

// Template List View
function TemplateListView({
  groupId,
  onEdit,
  onViewStats,
  onCreateNew,
  isMobile,
}: {
  groupId: string
  onEdit: (template: GiftRewardTemplateDto) => void
  onViewStats: (template: GiftRewardTemplateDto) => void
  onCreateNew: () => void
  isMobile: boolean
}) {
  const [filterTab, setFilterTab] = useState<FilterTab>('all')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<GiftRewardTemplateDto | null>(null)
  const [distributeDialogOpen, setDistributeDialogOpen] = useState(false)
  const [templateToDistribute, setTemplateToDistribute] = useState<GiftRewardTemplateDto | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<GiftRewardTemplateDto | null>(null)
  const [sortColumn, setSortColumn] = useState<SortColumn>('title')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const { data, isLoading, error } = useRewardTemplates(groupId)
  const toggleActiveMutation = useToggleTemplateActive()
  const deleteMutation = useDeleteTemplate()

  const templates = data?.items ?? []

  // Filter templates
  const filteredTemplates = templates.filter((t) => {
    if (filterTab === 'active') return t.isActive
    if (filterTab === 'inactive') return !t.isActive
    return true
  })

  // Sort templates
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    let comparison = 0
    switch (sortColumn) {
      case 'title':
        comparison = (a.title || '').localeCompare(b.title || '')
        break
      case 'contentType':
        comparison = a.contentType - b.contentType
        break
      case 'distributionType':
        comparison = a.distributionType - b.distributionType
        break
      case 'distributed':
        comparison = (a.totalDistributed ?? 0) - (b.totalDistributed ?? 0)
        break
      case 'active':
        comparison = (a.activeInstances ?? 0) - (b.activeInstances ?? 0)
        break
    }
    return sortDirection === 'asc' ? comparison : -comparison
  })

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const SortableHeader = ({ column, children, align = 'left', width }: { column: SortColumn; children: React.ReactNode; align?: 'left' | 'center' | 'right'; width?: number }) => (
    <TableCell
      sx={{
        color: 'white',
        fontWeight: 'bold',
        py: 1,
        cursor: 'pointer',
        userSelect: 'none',
        width,
        '&:hover': { backgroundColor: 'primary.dark' },
      }}
      align={align}
      onClick={() => handleSort(column)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start', gap: 0.5 }}>
        {children}
        {sortColumn === column && (
          sortDirection === 'asc' ? <ArrowUpward sx={{ fontSize: 16 }} /> : <ArrowDownward sx={{ fontSize: 16 }} />
        )}
      </Box>
    </TableCell>
  )

  const handleDistribute = (template: GiftRewardTemplateDto) => {
    setTemplateToDistribute(template)
    setDistributeDialogOpen(true)
  }

  const handleToggleActive = async (template: GiftRewardTemplateDto) => {
    await toggleActiveMutation.mutateAsync({ groupId, templateId: template.id })
  }

  const handleDeleteClick = (template: GiftRewardTemplateDto) => {
    setTemplateToDelete(template)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return
    await deleteMutation.mutateAsync({ groupId, templateId: templateToDelete.id })
    setDeleteDialogOpen(false)
    setTemplateToDelete(null)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: GiftRewardTemplateDto) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setSelectedTemplate(template)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
    setSelectedTemplate(null)
  }

  const handleMenuAction = (action: () => void) => {
    handleMenuClose()
    action()
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header - only show when templates exist */}
      {templates.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Reward Templates
          </Typography>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={onCreateNew}>
            Create Template
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load reward templates.
        </Alert>
      )}

      {/* Filter tabs */}
      <Paper elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={filterTab}
          onChange={(_, v) => setFilterTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="all" label="All" icon={<CardGiftcard />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab value="active" label="Active" icon={<CheckCircle />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab value="inactive" label="Inactive" icon={<Cancel />} iconPosition="start" sx={{ minHeight: 48 }} />
        </Tabs>
      </Paper>

      {/* Empty state */}
      {templates.length === 0 && (
        <Paper
          elevation={0}
          sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}
        >
          <CardGiftcard sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Reward Templates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first reward template to start distributing rewards.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={onCreateNew}>
            Create Template
          </Button>
        </Paper>
      )}

      {/* Templates table */}
      {filteredTemplates.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <SortableHeader column="title">Template</SortableHeader>
                <SortableHeader column="contentType" align="center" width={40}>Type</SortableHeader>
                <SortableHeader column="distributionType" align="center" width={40}>Dist</SortableHeader>
                <SortableHeader column="distributed" width={80}>Distributed</SortableHeader>
                <SortableHeader column="active" width={60}>Active</SortableHeader>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1, width: 80 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTemplates.map((template) => {
                const ContentIcon = getContentTypeIcon(template.contentType)
                const DistIcon = getDistributionTypeIcon(template.distributionType)
                return (
                  <TableRow
                    key={template.id}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      opacity: template.isActive ? 1 : 0.6,
                    }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                            {template.title || 'Untitled'}
                          </Typography>
                          {template.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                maxWidth: 200,
                              }}
                            >
                              {template.description}
                            </Typography>
                          )}
                        </Box>
                        {!template.isActive && (
                          <Chip label="Inactive" size="small" variant="outlined" sx={{ ml: 'auto', height: 20, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Tooltip title={getContentTypeName(template.contentType)}>
                        <ContentIcon fontSize="small" color={getContentTypeColor(template.contentType)} />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Tooltip title={getDistributionTypeName(template.distributionType)}>
                        <DistIcon fontSize="small" color="action" />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2">
                        {template.totalDistributed ?? 0}
                        {template.totalLimit && ` / ${template.totalLimit}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2">
                        {template.activeInstances ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Stats">
                          <IconButton size="small" onClick={() => onViewStats(template)}>
                            <BarChart fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, template)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* No results for filter */}
      {filteredTemplates.length === 0 && templates.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
          <Typography color="text.secondary">
            No {filterTab === 'active' ? 'active' : 'inactive'} templates found.
          </Typography>
        </Paper>
      )}

      {/* Actions menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {selectedTemplate?.isActive && (
          <MenuItem onClick={() => selectedTemplate && handleMenuAction(() => handleDistribute(selectedTemplate))}>
            <ListItemIcon>
              <Send fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Distribute</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => selectedTemplate && handleMenuAction(() => onEdit(selectedTemplate))}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedTemplate && handleMenuAction(() => handleToggleActive(selectedTemplate))}>
          <ListItemIcon>
            {selectedTemplate?.isActive ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{selectedTemplate?.isActive ? 'Deactivate' : 'Activate'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => selectedTemplate && handleMenuAction(() => handleDeleteClick(selectedTemplate))}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{templateToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </Dialog>

      {/* Distribute dialog */}
      <DistributeRewardsDialog
        open={distributeDialogOpen}
        onClose={() => {
          setDistributeDialogOpen(false)
          setTemplateToDistribute(null)
        }}
        template={templateToDistribute}
      />
    </Box>
  )
}

// Template Edit View
function TemplateEditView({
  groupId,
  templateId,
  onBack,
  onSaved,
  isMobile,
}: {
  groupId: string
  templateId?: string
  onBack: () => void
  onSaved: () => void
  isMobile: boolean
}) {
  const isEditing = !!templateId
  const { data: existingTemplate, isLoading: isLoadingTemplate } = useRewardTemplate(groupId, templateId)

  const createMutation = useCreateTemplate()
  const updateMutation = useUpdateTemplate()

  const [formData, setFormData] = useState<CreateGiftRewardTemplateRequest>({
    groupId: groupId || null,
    title: '',
    description: '',
    contentType: GiftRewardContentType.Text,
    content: null,
    distributionType: DistributionType.Manual,
    distributionConfig: null,
    totalLimit: null,
    dailyLimit: null,
    weeklyLimit: null,
    yearlyLimit: null,
    usesPerInstance: 1,
    isTransferable: false,
    allowPostExpirationRedemption: false,
    expirationType: ExpirationType.Never,
    expirationDays: null,
    fixedExpirationDate: null,
    authorizedScannerGroupId: null,
  })
  const [error, setError] = useState<string | null>(null)
  const [textContent, setTextContent] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null)
  const [isUploadingImage, setIsUploadingImage] = useState(false)

  // Adventure Unlock state
  const [adventureSearchQuery, setAdventureSearchQuery] = useState('')
  const [adventureSearchResults, setAdventureSearchResults] = useState<AdventureDto[]>([])
  const [isSearchingAdventures, setIsSearchingAdventures] = useState(false)
  const [selectedAdventure, setSelectedAdventure] = useState<{
    id: string
    title: string
    thumbnailUrl?: string
    quickDescription?: string
  } | null>(null)

  // TipTap editor for rich text content
  const textEditor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          link: false,
          underline: false,
        }),
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        BorderlessTable.configure({ resizable: true }),
        TiptapTableRow,
        TiptapTableCell,
        TiptapTableHeader,
        ResizableImage.configure({
          inline: false,
          allowBase64: true,
        }),
        Link.configure({
          openOnClick: false,
        }),
        ImagePasteHandler.configure({
          maxWidth: 600,
          maxHeight: 600,
          quality: 0.8,
        }),
      ],
      content: textContent,
      onUpdate: ({ editor }) => {
        setTextContent(editor.getHTML())
      },
    },
    []
  )

  // Update editor content when textContent changes externally (e.g., loading existing template)
  useEffect(() => {
    if (textEditor && textContent && textEditor.getHTML() !== textContent) {
      textEditor.commands.setContent(textContent)
    }
  }, [textContent, textEditor])

  // Cleanup editor on unmount
  useEffect(() => {
    return () => {
      textEditor?.destroy()
    }
  }, [textEditor])

  useEffect(() => {
    if (existingTemplate) {
      setFormData({
        groupId: existingTemplate.groupId,
        title: existingTemplate.title || '',
        description: existingTemplate.description || '',
        contentType: existingTemplate.contentType,
        content: existingTemplate.content,
        distributionType: existingTemplate.distributionType,
        distributionConfig: existingTemplate.distributionConfig,
        totalLimit: existingTemplate.totalLimit,
        dailyLimit: existingTemplate.dailyLimit,
        weeklyLimit: existingTemplate.weeklyLimit,
        yearlyLimit: existingTemplate.yearlyLimit,
        usesPerInstance: existingTemplate.usesPerInstance,
        isTransferable: existingTemplate.isTransferable,
        allowPostExpirationRedemption: existingTemplate.allowPostExpirationRedemption,
        expirationType: existingTemplate.expirationType,
        expirationDays: existingTemplate.expirationDays,
        fixedExpirationDate: existingTemplate.fixedExpirationDate,
        authorizedScannerGroupId: existingTemplate.authorizedScannerGroupId,
      })
      const content = existingTemplate.content as Record<string, unknown> | null
      if (content) {
        setTextContent((content.textContent as string) || '')
        setLinkUrl((content.linkUrl as string) || '')
        setImageUrl((content.imageUrl as string) || '')
        // Load selected adventure for Adventure Unlock type
        if (content.adventureId && content.adventureTitle) {
          setSelectedAdventure({
            id: content.adventureId as string,
            title: content.adventureTitle as string,
          })
        }
      }
    }
  }, [existingTemplate])

  // Handle image file selection
  const handleImageSelect = (file: File) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setImageUrl(reader.result as string)
      setUploadedImageFile(file)
    }
    reader.readAsDataURL(file)
  }

  // Handle adventure search
  const handleSearchAdventures = useCallback(async (query: string) => {
    if (!query.trim()) {
      setAdventureSearchResults([])
      return
    }

    setIsSearchingAdventures(true)
    try {
      const response = await adventuresApi.searchAdventures({
        searchTerm: query,
        myAdventuresOnly: true,
        pageSize: 10,
        page: 1,
      })
      const adventures = (response as any).adventures || response.items || []
      setAdventureSearchResults(adventures)
    } catch (error) {
      console.error('Failed to search adventures:', error)
      setAdventureSearchResults([])
    } finally {
      setIsSearchingAdventures(false)
    }
  }, [])

  // Handle adventure selection
  const handleSelectAdventure = (adventure: AdventureDto) => {
    setSelectedAdventure({
      id: adventure.id,
      title: adventure.title,
      thumbnailUrl: (adventure as any).thumbnailUrl,
      quickDescription: (adventure as any).quickDescription,
    })
    setAdventureSearchQuery('')
    setAdventureSearchResults([])
  }

  // Clear selected adventure
  const handleClearAdventure = () => {
    setSelectedAdventure(null)
    setAdventureSearchQuery('')
    setAdventureSearchResults([])
  }

  const handleSubmit = async () => {
    setError(null)

    let finalImageUrl = imageUrl

    // Upload image file if selected
    if (formData.contentType === GiftRewardContentType.Image && uploadedImageFile) {
      try {
        setIsUploadingImage(true)
        finalImageUrl = await uploadsApi.uploadImage(uploadedImageFile, 'rewards')
      } catch (err: any) {
        setError('Failed to upload image: ' + (err.message || 'Unknown error'))
        setIsUploadingImage(false)
        return
      }
      setIsUploadingImage(false)
    }

    let content: Record<string, unknown> | null = null
    switch (formData.contentType) {
      case GiftRewardContentType.Text:
        content = { textContent }
        break
      case GiftRewardContentType.URL:
        content = { linkUrl }
        break
      case GiftRewardContentType.Image:
        content = { imageUrl: finalImageUrl }
        break
      case GiftRewardContentType.QR_Redeemable:
        content = {}
        break
      case GiftRewardContentType.Adventure_Unlock:
        content = selectedAdventure
          ? { adventureId: selectedAdventure.id, adventureTitle: selectedAdventure.title }
          : {}
        break
    }

    const submitData: CreateGiftRewardTemplateRequest = { ...formData, content }

    try {
      if (isEditing && templateId) {
        await updateMutation.mutateAsync({ groupId, templateId, data: submitData })
      } else {
        await createMutation.mutateAsync({ groupId, data: submitData })
      }
      onSaved()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save template')
    }
  }

  if (isEditing && isLoadingTemplate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  const isPending = createMutation.isPending || updateMutation.isPending || isUploadingImage

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        Back to Templates
      </Button>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {isEditing ? 'Edit Template' : 'Create Template'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={isMobile ? 12 : 8}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Basic Information
            </Typography>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              size="small"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              size="small"
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Content
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={formData.contentType}
                label="Content Type"
                onChange={(e) => setFormData({ ...formData, contentType: e.target.value as GiftRewardContentType })}
              >
                {Object.values(GiftRewardContentType)
                  .filter((v) => typeof v === 'number')
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      {getContentTypeName(type as GiftRewardContentType)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {formData.contentType === GiftRewardContentType.Text && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Create formatted text with styling, links, and images.
                </Typography>
                <Box className="rich-editor-container rich-editor-compact">
                  {textEditor && (
                    <TipTapToolbar
                      editor={textEditor}
                      onFullscreen={() => {}}
                      isFullscreen={false}
                    />
                  )}
                  <EditorContent editor={textEditor} className="tiptap-editor tiptap-editor-compact" />
                  {textEditor && <ImageBubbleMenu editor={textEditor} />}
                </Box>
              </Box>
            )}
            {formData.contentType === GiftRewardContentType.URL && (
              <TextField
                fullWidth
                label="Link URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                type="url"
                size="small"
              />
            )}
            {formData.contentType === GiftRewardContentType.Image && (
              <ImageUploadZone
                value={imageUrl || null}
                onFileSelect={handleImageSelect}
                onClear={() => {
                  setImageUrl('')
                  setUploadedImageFile(null)
                }}
                maxSizeMB={5}
                label="Upload Reward Image"
                description="Drag and drop, paste, or click to select"
                isUploading={isUploadingImage}
                disabled={isPending}
              />
            )}
            {formData.contentType === GiftRewardContentType.QR_Redeemable && (
              <Alert severity="info" sx={{ mt: 1 }}>
                QR Redeemable rewards generate a unique scannable code.
              </Alert>
            )}
            {formData.contentType === GiftRewardContentType.Adventure_Unlock && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Select an adventure that will be unlocked for the user when they claim this reward.
                </Typography>
                {selectedAdventure ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedAdventure.title}
                      </Typography>
                      <IconButton size="small" onClick={handleClearAdventure} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ) : (
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search your adventures"
                      placeholder="Type to search..."
                      value={adventureSearchQuery}
                      onChange={(e) => {
                        setAdventureSearchQuery(e.target.value)
                        handleSearchAdventures(e.target.value)
                      }}
                      InputProps={{
                        endAdornment: isSearchingAdventures ? (
                          <CircularProgress size={20} />
                        ) : null,
                      }}
                    />
                    {adventureSearchResults.length > 0 && (
                      <Paper variant="outlined" sx={{ mt: 1, maxHeight: 250, overflow: 'auto' }}>
                        <List dense disablePadding>
                          {adventureSearchResults.map((adventure) => (
                            <ListItem
                              key={adventure.id}
                              component="div"
                              onClick={() => handleSelectAdventure(adventure)}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' },
                                py: 1,
                              }}
                            >
                              <Box sx={{ display: 'flex', gap: 1.5, width: '100%', alignItems: 'center' }}>
                                {(adventure as any).thumbnailUrl ? (
                                  <Box
                                    component="img"
                                    src={(adventure as any).thumbnailUrl}
                                    alt=""
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: 1,
                                      objectFit: 'cover',
                                      flexShrink: 0,
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: 1,
                                      bgcolor: 'action.hover',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <CardGiftcard sx={{ color: 'text.secondary', fontSize: 20 }} />
                                  </Box>
                                )}
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="body2" fontWeight="medium" noWrap>
                                    {adventure.title}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {(adventure as any).quickDescription || ''}
                                  </Typography>
                                </Box>
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                    {adventureSearchQuery && !isSearchingAdventures && adventureSearchResults.length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        No adventures found. Make sure you own or have access to the adventure.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Distribution
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Distribution Type</InputLabel>
              <Select
                value={formData.distributionType}
                label="Distribution Type"
                onChange={(e) => setFormData({ ...formData, distributionType: e.target.value as DistributionType })}
              >
                {Object.values(DistributionType)
                  .filter((v) => typeof v === 'number')
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      {getDistributionTypeName(type as DistributionType)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <DistributionConfigEditor
              distributionType={formData.distributionType}
              config={formData.distributionConfig as unknown as DistributionConfig}
              onChange={(config) => setFormData({ ...formData, distributionConfig: config as unknown as Record<string, unknown> })}
            />
            <TextField
              fullWidth
              label="Total Limit"
              type="number"
              value={formData.totalLimit || ''}
              onChange={(e) => setFormData({ ...formData, totalLimit: e.target.value ? parseInt(e.target.value) : null })}
              helperText="Leave empty for unlimited"
              size="small"
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={isMobile ? 12 : 4}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Instance Settings
            </Typography>
            <TextField
              fullWidth
              label="Uses Per Instance"
              type="number"
              value={formData.usesPerInstance || ''}
              onChange={(e) => setFormData({ ...formData, usesPerInstance: e.target.value ? parseInt(e.target.value) : null })}
              size="small"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Expiration Type</InputLabel>
              <Select
                value={formData.expirationType}
                label="Expiration Type"
                onChange={(e) => setFormData({ ...formData, expirationType: e.target.value as ExpirationType })}
              >
                {Object.values(ExpirationType)
                  .filter((v) => typeof v === 'number')
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      {getExpirationTypeName(type as ExpirationType)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {formData.expirationType === ExpirationType.DaysFromClaim && (
              <TextField
                fullWidth
                label="Days Until Expiration"
                type="number"
                value={formData.expirationDays || ''}
                onChange={(e) => setFormData({ ...formData, expirationDays: e.target.value ? parseInt(e.target.value) : null })}
                size="small"
                sx={{ mb: 2 }}
              />
            )}
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isTransferable}
                  onChange={(e) => setFormData({ ...formData, isTransferable: e.target.checked })}
                  size="small"
                />
              }
              label="Allow Transfer"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.allowPostExpirationRedemption}
                  onChange={(e) => setFormData({ ...formData, allowPostExpirationRedemption: e.target.checked })}
                  size="small"
                />
              }
              label="Allow Post-Expiration"
            />
          </Paper>

          <Button
            fullWidth
            variant="contained"
            startIcon={isPending ? <CircularProgress size={18} /> : <Save />}
            onClick={handleSubmit}
            disabled={isPending || !formData.title}
          >
            {isPending ? 'Saving...' : isEditing ? 'Update Template' : 'Create Template'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

// Template Stats View
function TemplateStatsView({
  groupId,
  templateId,
  onBack,
  isMobile,
}: {
  groupId: string
  templateId: string
  onBack: () => void
  isMobile: boolean
}) {
  const [activeTab, setActiveTab] = useState(0)

  const { data: template, isLoading: isLoadingTemplate } = useRewardTemplate(groupId, templateId)
  const { data: stats, isLoading: isLoadingStats } = useTemplateStats(groupId, templateId)
  const { data: redemptions, isLoading: isLoadingRedemptions } = useRedemptionHistory(groupId, templateId)
  const { data: transfers, isLoading: isLoadingTransfers } = useTransferHistory(groupId, templateId)

  if (isLoadingTemplate || isLoadingStats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!template) {
    return <Alert severity="error">Template not found</Alert>
  }

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        Back to Templates
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CardGiftcard color="primary" />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {template.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Template Statistics
          </Typography>
        </Box>
      </Box>

      {/* Stats Overview */}
      {stats && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <StatsCard
              icon={<CardGiftcard />}
              label="Distributed"
              value={stats.totalDistributed}
              color="primary.main"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard
              icon={<CheckCircle />}
              label="Active"
              value={stats.activeInstances}
              color="success.main"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard
              icon={<Redeem />}
              label="Redeemed"
              value={stats.totalRedeemedUses}
              color="info.main"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard
              icon={<Schedule />}
              label="Expired"
              value={stats.expiredInstances}
              color="warning.main"
            />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<People />} label="Holders" iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab icon={<Redeem />} label="Redemptions" iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab icon={<SwapHoriz />} label="Transfers" iconPosition="start" sx={{ minHeight: 48 }} />
        </Tabs>
      </Paper>

      {/* Current Holders */}
      {activeTab === 0 && stats?.currentHolders && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Claimed</TableCell>
                <TableCell>Uses</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.currentHolders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No current holders
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                stats.currentHolders.map((holder: CurrentHolderDto, index: number) => (
                  <TableRow key={holder.userId || index}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24 }}>
                          <Person sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography variant="body2">{holder.displayName || 'Unknown'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{format(new Date(holder.claimedAt), 'MMM d, yyyy')}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{holder.remainingUses ?? 'Unlimited'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={getInstanceStatusName(holder.status)} size="small" color={getStatusColor(holder.status)} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Redemptions */}
      {activeTab === 1 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Redeemed By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Uses</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingRedemptions ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress size={20} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : redemptions?.items?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No redemptions yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                redemptions?.items?.map((redemption) => (
                  <TableRow key={redemption.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24 }}>
                          <Person sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography variant="body2">{redemption.scannerDisplayName || 'Unknown'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{format(new Date(redemption.redeemedAt), 'MMM d h:mm a')}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{redemption.usesConsumed}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Transfers */}
      {activeTab === 2 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingTransfers ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress size={20} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : transfers?.items?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No transfers yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transfers?.items?.map((transfer) => (
                  <TableRow key={transfer.id}>
                    <TableCell>
                      <Typography variant="body2">{transfer.fromUserDisplayName || 'Unknown'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{transfer.toUserDisplayName || 'Unknown'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{format(new Date(transfer.transferredAt), 'MMM d h:mm a')}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

// Main Dialog Component
export function GroupRewardsDialog({ open, onClose, groupId }: GroupRewardsDialogProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [view, setView] = useState<DialogView>('list')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>()

  const handleClose = () => {
    setView('list')
    setSelectedTemplateId(undefined)
    onClose()
  }

  const handleEdit = (template: GiftRewardTemplateDto) => {
    setSelectedTemplateId(template.id)
    setView('edit')
  }

  const handleViewStats = (template: GiftRewardTemplateDto) => {
    setSelectedTemplateId(template.id)
    setView('stats')
  }

  const handleCreateNew = () => {
    setSelectedTemplateId(undefined)
    setView('edit')
  }

  const handleBackToList = () => {
    setSelectedTemplateId(undefined)
    setView('list')
  }

  const handleSaved = () => {
    setSelectedTemplateId(undefined)
    setView('list')
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          minHeight: isMobile ? undefined : '60vh',
          maxHeight: isMobile ? undefined : '85vh',
          borderRadius: isMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CardGiftcard color="primary" />
          <Typography variant="h6">Manage Rewards</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={isMobile ? { px: 1.5 } : undefined}>
        {view === 'list' && (
          <TemplateListView
            groupId={groupId}
            onEdit={handleEdit}
            onViewStats={handleViewStats}
            onCreateNew={handleCreateNew}
            isMobile={isMobile}
          />
        )}

        {view === 'edit' && (
          <TemplateEditView
            groupId={groupId}
            templateId={selectedTemplateId}
            onBack={handleBackToList}
            onSaved={handleSaved}
            isMobile={isMobile}
          />
        )}

        {view === 'stats' && selectedTemplateId && (
          <TemplateStatsView
            groupId={groupId}
            templateId={selectedTemplateId}
            onBack={handleBackToList}
            isMobile={isMobile}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
